<template>
  <nav class="ui secondary pointing menu" id="application-menu">
    <router-link
      class="item"
      exact
      v-if="app && app.id"
      :to="{ name: 'app-settings', params: { appId: app.id } }"
      :active-class="'active'"
    >
      <div class="text ellipsis" :style="{ 'max-width': 400 + 'px' }">
        {{ (app && app.name) || $t('SETTINGS') }}
      </div>
    </router-link>
    <div
      class="ui secondary compact menu"
      @mouseenter="flowMenuHovered = true"
      @mouseleave="flowMenuHovered = false"
    >
      <div
        class="ui simple dropdown item"
        :class="{ selected: flowName }"
        style="max-width: 400px;"
      >
        <i class="random icon"></i>
        <span class="ellipsis">{{ flowName || ld.capitalize($tc('FLOW', 2)) }}</span>
        <multi-color-label class="multiColorLabel" size="tiny" :colors="flowGroupsColors" />
        <i class="dropdown icon"></i>
        <flow-menu
          v-if="flowMenuHovered"
          style="min-width: 200px; max-width: 400px;"
          :app="app"
          :groupColorsByFlow="groupColorsByFlow"
          @onGroupFilterAdd="onGroupFilterAdd"
          @onGroupFilterRemove="onGroupFilterRemove"
          @onMaintainerFilterAdd="onMaintainerFilterAdd"
          @onMaintainerFilterRemove="onMaintainerFilterRemove"
          :filterGroups="filterGroups"
          :filterMaintainers="filterMaintainers"
        />
      </div>
    </div>
    <div class="ui secondary compact menu" v-if="app.type !== 'HEADLESS'">
      <div class="ui simple dropdown item" :class="{ selected: field }" style="max-width: 400px;">
        <i class="bookmark icon"></i>
        <span class="ellipsis">{{ (field && field.name) || ld.capitalize($tc('FIELD', 2)) }}</span>
        <i class="dropdown icon"></i>
        <div class="compact menu" style="min-width: 200px; max-width: 400px;">
          <div class="header">{{ $tc('ACTION', 2) }}</div>
          <router-link class="item" :to="{ name: 'new-field' }">
            <i class="plus icon"></i>
            {{ $t('NEW', { thing: $tc('FIELD', 1) }) }}
          </router-link>
          <div class="divider"></div>
          <!-- Search -->
          <div class="header" v-if="showFieldSearch">
            {{ $t('SEARCH', { thing: $tc('FIELD', 2) }) }}
          </div>
          <div class="ui icon input" v-if="showFieldSearch">
            <i class="search icon"></i>
            <input
              type="text"
              name="fieldSearch"
              :placeholder="$t('SEARCH')"
              v-model="searchTerm"
              @keydown.esc="searchTerm = ''"
              autocomplete="off"
            />
          </div>
          <div class="divider" v-if="showFieldSearch"></div>
          <!-- Fields -->
          <div class="header">
            {{ $tc('FIELD', items.length) }}
            <span>({{ filteredItems.length }}/{{ items.length }})</span>
          </div>
          <div class="scroller fuzzy-list">
            <Spinny :promise="fieldsPromise" />
            <router-link
              class="item"
              :title="field.string"
              :to="{
                name: 'app-field',
                params: {
                  appId: app.id,
                  fieldId: field.original.id
                }
              }"
              :active-class="'selected'"
              v-for="field in filteredItems"
              :key="field.original.id"
            >
              <div class="scrollerItem">
                <span class="text ellipsis" v-html="field.string" />
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<style lang="scss">
// Note: *not* scoped because it is shared with the child component FlowMenu
@charset "utf-8";
@import '../../scss/variables';

#application-menu {
  .item {
    line-height: 1.1em;
  }

  .scrollerItem {
    display: flex;
  }

  .multiColorLabel {
    margin-left: 0.5em;
  }

  // Otherwise dropdown is near impossible to hit
  .ui.secondary.menu .dropdown.item > .menu,
  .ui.text.menu .dropdown.item > .menu {
    margin-top: 0px;

    .item:hover {
      background-color: $g9;
    }
  }

  .ui.secondary.pointing.menu .selected.dropdown.item {
    border-radius: 0px;
    border-color: #1b1c1d;
  }

  .item.selected > span {
    font-weight: bold;
  }

  .ui.compact.menu .scroller {
    max-height: 300px;
    overflow-y: scroll;
  }
}
</style>

<script>
// @flow
import _ from 'lodash'
import { Field, Group } from 'services'
import { Spinny, MultiColorLabel } from 'ui'
import FlowMenu from './FlowMenu'
import { MutationTypes } from 'store'
import FuzzyFilterer from '../Mixins/FuzzyFilterer'

let lastAppId = ''

export default {
  name: 'application-menu',
  components: { Spinny, MultiColorLabel, FlowMenu },
  props: ['app', 'flowName', 'field', 'flowGroups', 'mode'],
  mixins: [FuzzyFilterer],

  data() {
    return {
      ld: _,
      filterGroups: [],
      filterMaintainers: [],
      fieldsPromise: null,
      flowMenuHovered: false
    }
  },

  computed: {
    // Field search term: override searchTerm property in FuzzyFilterer
    searchTerm: {
      get() {
        return this.$store.state.fieldSearchTerm || ''
      },
      set(v) {
        this.$store.commit(MutationTypes.SET_FIELD_SEARCH_TERM, v)
      }
    },
    groups() {
      return this.$store.state.group.items
    },
    // Fields for the FuzzyFilterer mixin
    items() {
      return this.app.id ? this.$store.getters['field/getItemsForApp'](this.app.id) : []
    },
    showFieldSearch() {
      return this.items && this.items.length > 10
    },
    flowGroupsColors() {
      return _.filter(
        _.map(this.flowGroups, group => group.color),
        color => color !== Group.defaultColor
      )
    },
    groupColorsByFlow() {
      const grouped = {}
      _.each(this.groups, g =>
        _.each(g.flow, flowId => {
          if (!grouped[flowId]) grouped[flowId] = [g.color]
          else grouped[flowId].push(g.color)
        })
      )
      return grouped
    }
  },

  methods: {
    resetSearchTerms() {
      this.$store.commit(MutationTypes.SET_FLOW_SEARCH_TERM, '')
      this.$store.commit(MutationTypes.SET_FIELD_SEARCH_TERM, '')
    },

    onGroupFilterAdd(group) {
      this.filterGroups.push(group)
    },

    onGroupFilterRemove(group) {
      this.filterGroups = this.filterGroups.filter(g => g.id !== group.id)
    },

    onMaintainerFilterAdd(maintainer) {
      this.filterMaintainers.push(maintainer)
    },

    onMaintainerFilterRemove(maintainer) {
      this.filterMaintainers = this.filterMaintainers.filter(m => m.id !== maintainer.id)
    }
  },

  created() {
    Group.readAllGroups()
    this.fieldsPromise = Field.readFields()
    if (lastAppId && this.app && this.app.id && lastAppId !== this.app.id) {
      this.resetSearchTerms()
    }
    lastAppId = this.app.id || lastAppId
  },

  watch: {
    app(newApp, oldApp) {
      // Detect change of app under a variety of circumstances
      if (
        (oldApp && oldApp.id && oldApp.id !== newApp.id) ||
        (lastAppId && !oldApp) ||
        (lastAppId && newApp && newApp.id !== lastAppId)
      ) {
        this.resetSearchTerms()
      }
      lastAppId = newApp.id || lastAppId
    }
  }
}
</script>
